<template>
  <main class="main">
    <form class="form" @submit.prevent="createUpdateEquipement">
      <fieldset class="fieldset">
        <legend class="legend">
          <PhTruck />
          <span>Informations</span>
        </legend>

        <div class="field">
          <label class="label">Nom</label>
          <input type="text" class="input" v-model="equipement.nom" required />
        </div>

        <div class="field">
          <label class="label">Catégorie</label>
          <select class="select" v-model="equipement.categorie" required>
            <option v-for="categorie in categories" :key="categorie.id" :value="categorie.id">
              {{ categorie.nom }}
            </option>
          </select>
        </div>
      </fieldset>
      <div class="buttons">
        <ButtonElement
          type="submit"
          color="bordeaux"
          :icon="PhFloppyDisk"
          :disabled="!cacheStore.online"
        >
          {{ equipementId ? 'Modifier' : 'Créer' }}
        </ButtonElement>
      </div>
    </form>
  </main>
</template>

<script setup lang="ts">
import { PhFloppyDisk, PhTruck } from '@phosphor-icons/vue'
import ButtonElement from '@/components/ButtonElement.vue'
import { computed, type ComputedRef, onMounted, ref, type Ref } from 'vue'
import type {
  EquipementCategorie,
  EquipementCreate,
  EquipementUpdate,
} from '@/interfaces/Equipement.ts'
import { useRoute, useRouter } from 'vue-router'
import useFetch from '@/composables/useFetch.ts'
import { useLoadingStore } from '@/stores/loading.ts'
import { useToastStore } from '@/stores/toast.ts'
import { useCacheStore } from '@/stores/cache.ts'

// dépendances
const api = useFetch()
const route = useRoute()
const router = useRouter()

// stores
const cacheStore = useCacheStore()
const loadingStore = useLoadingStore()
const toastStore = useToastStore()

// données
const equipement: Ref<EquipementCreate | EquipementUpdate> = ref({
  id: '',
  nom: '',
  categorie: '',
})
const categories: Ref<EquipementCategorie[]> = ref([])

// computed
const equipementId: ComputedRef<string | undefined> = computed(() => route.params.id?.toString())

// fonctions
async function getCategories() {
  categories.value = await cacheStore.getEquipementsCategories()
}

async function getEquipement() {
  if (equipementId.value) equipement.value = await cacheStore.getEquipement(equipementId.value)
}

async function createUpdateEquipement() {
  loadingStore.start()
  try {
    if (equipementId.value) {
      await api.put(`equipements/${equipementId.value}`, equipement.value)
    } else {
      await api.post('equipements/', equipement.value)
    }
    toastStore.success(`L'équipement a bien été ${equipementId.value ? 'modifié' : 'créé'}.`)
    await router.push({ name: 'equipements' })
  } finally {
    loadingStore.stop()
  }
}

onMounted(async () => {
  loadingStore.start()
  try {
    await getCategories()
    await getEquipement()
  } finally {
    loadingStore.stop()
  }
})
</script>
